'use client';

import React from 'react';
import { Phone, PhoneOff, PhoneCall, Video, VideoOff, Mic, MicOff } from 'lucide-react';
import { CallStatus, CallType } from '@/hooks/useCall';
import { Button } from '@/components/ui/Button';

interface CallUIProps {
  callStatus: CallStatus;
  callType: CallType;
  incomingType: CallType;
  localVideoRef: React.RefObject<HTMLVideoElement>;
  remoteVideoRef: React.RefObject<HTMLVideoElement>;
  onAccept: () => void;
  onReject: () => void;
  onEnd: () => void;
}

export function CallUI({
  callStatus,
  callType,
  incomingType,
  localVideoRef,
  remoteVideoRef,
  onAccept,
  onReject,
  onEnd,
}: CallUIProps) {
  if (callStatus === 'idle') return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 backdrop-blur-md">
      {/* Incoming call */}
      {callStatus === 'receiving' && (
        <div className="flex flex-col items-center gap-6 text-center px-6">
          <div className="w-20 h-20 rounded-full bg-brand-lime/10 border-2 border-brand-lime/40 flex items-center justify-center animate-pulse">
            {incomingType === 'video' ? (
              <Video size={36} className="text-brand-lime" />
            ) : (
              <Phone size={36} className="text-brand-lime" />
            )}
          </div>
          <div>
            <p className="text-brand-white text-xl font-semibold">
              Incoming {incomingType === 'video' ? 'Video' : 'Audio'} Call
            </p>
            <p className="text-brand-gray text-sm mt-1">Stranger wants to {incomingType === 'video' ? 'video' : 'voice'} chat</p>
          </div>
          <div className="flex items-center gap-6 mt-2">
            <button
              onClick={onReject}
              className="flex flex-col items-center gap-2 group"
            >
              <div className="w-14 h-14 rounded-full bg-red-500/20 hover:bg-red-500/40 border border-red-500/40 flex items-center justify-center transition-colors">
                <PhoneOff size={22} className="text-red-400" />
              </div>
              <span className="text-xs text-brand-gray group-hover:text-brand-white transition-colors">Decline</span>
            </button>
            <button
              onClick={onAccept}
              className="flex flex-col items-center gap-2 group"
            >
              <div className="w-14 h-14 rounded-full bg-brand-lime/20 hover:bg-brand-lime/40 border border-brand-lime/40 flex items-center justify-center transition-colors">
                <Phone size={22} className="text-brand-lime" />
              </div>
              <span className="text-xs text-brand-gray group-hover:text-brand-white transition-colors">Accept</span>
            </button>
          </div>
        </div>
      )}

      {/* Outgoing call (waiting for accept) */}
      {callStatus === 'calling' && (
        <div className="flex flex-col items-center gap-6 text-center px-6">
          <div className="w-20 h-20 rounded-full bg-brand-lime/10 border-2 border-brand-lime/30 flex items-center justify-center">
            <PhoneCall size={36} className="text-brand-lime animate-pulse" />
          </div>
          <div>
            <p className="text-brand-white text-xl font-semibold">Calling...</p>
            <p className="text-brand-gray text-sm mt-1">Waiting for stranger to accept your {callType === 'video' ? 'video' : 'voice'} call</p>
          </div>
          <button
            onClick={onEnd}
            className="flex flex-col items-center gap-2 group"
          >
            <div className="w-14 h-14 rounded-full bg-red-500/20 hover:bg-red-500/40 border border-red-500/40 flex items-center justify-center transition-colors">
              <PhoneOff size={22} className="text-red-400" />
            </div>
            <span className="text-xs text-brand-gray">Cancel</span>
          </button>
        </div>
      )}

      {/* Active call */}
      {callStatus === 'active' && (
        <div className="w-full h-full flex flex-col">
          {/* Remote video (full screen) */}
          <div className="flex-1 relative bg-brand-black">
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
            {/* No remote video placeholder */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {callType === 'audio' && (
                <div className="flex flex-col items-center gap-3 opacity-60">
                  <div className="w-20 h-20 rounded-full bg-brand-surface-2 border border-white/10 flex items-center justify-center">
                    <Mic size={32} className="text-brand-gray" />
                  </div>
                  <p className="text-brand-gray text-sm">Voice call in progress</p>
                </div>
              )}
            </div>

            {/* Local video (PiP) */}
            {callType === 'video' && (
              <div className="absolute bottom-20 right-4 w-28 h-36 rounded-xl overflow-hidden border-2 border-white/20 shadow-lg bg-brand-black">
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover scale-x-[-1]"
                />
              </div>
            )}
          </div>

          {/* Call controls */}
          <div className="h-24 bg-brand-black/90 backdrop-blur-sm flex items-center justify-center gap-6 px-6 border-t border-white/[0.06]">
            <button
              onClick={onEnd}
              className="flex flex-col items-center gap-1.5 group"
            >
              <div className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg">
                <PhoneOff size={22} className="text-white" />
              </div>
              <span className="text-xs text-brand-gray group-hover:text-brand-white transition-colors">End</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
